type UploadProgressArgs = {
  bytesSent: number;
  bytesTotal: number;
  bytesPerSecond: number;
};

export type UploadProgressValues = UploadProgressArgs & {
  percentage: number;
  remainingTime: number;
};

const initialValues = {
  bytesSent: 0,
  bytesTotal: 0,
  bytesPerSecond: 0,
};

/**
 * A simple interface to store/manipulate upload progress data and
 * calculates the estimated time to finish uploading.
 */
export class UploadProgress {
  bytesSent: number = initialValues.bytesSent;

  bytesTotal: number = initialValues.bytesTotal;

  bytesPerSecond: number = initialValues.bytesPerSecond;

  get remainingTime() {
    const { bytesSent, bytesTotal, bytesPerSecond } = this;

    if (bytesSent === bytesTotal) return 0;

    return bytesPerSecond
      ? Math.round((bytesTotal - bytesSent) / bytesPerSecond)
      : Infinity;
  }

  get percentage() {
    const { bytesSent, bytesTotal } = this;
    if (!bytesSent) return 0;
    return Math.floor((bytesSent / bytesTotal) * 100);
  }

  get values(): UploadProgressValues {
    const { bytesSent, bytesTotal, bytesPerSecond, percentage, remainingTime } =
      this;

    return {
      bytesSent,
      bytesTotal,
      bytesPerSecond,
      percentage,
      remainingTime,
    };
  }

  constructor(args: UploadProgressArgs = initialValues) {
    Object.assign(this, args);
  }

  addBytesSent(bytesSent: number) {
    const remainingBytes = this.bytesTotal - this.bytesSent;
    if (bytesSent > remainingBytes) {
      const msg = `Cannot add more bytesSent (${bytesSent}) than what remains (${remainingBytes})`;
      throw new Error(msg);
    }
    this.bytesSent += bytesSent;
  }

  removeBytesSent(bytesSent: number) {
    if (bytesSent > this.bytesSent) {
      throw new Error('Cannot remove more bytes than added');
    }
    this.bytesSent -= bytesSent;
  }

  addBytesTotal(bytesTotal: number) {
    this.bytesTotal += bytesTotal;
  }

  removeBytesTotal(bytesTotal: number) {
    if (bytesTotal > this.bytesTotal) {
      throw new Error('Cannot remove more bytes than added');
    }
    this.bytesTotal -= bytesTotal;
  }

  clearUploadSpeed() {
    this.bytesPerSecond = 0;
  }

  clearAll() {
    Object.assign(this, initialValues);
  }
}
